import { Link } from "react-router-dom";
import { Server, AlertCircle, RefreshCw } from "lucide-react";
import { useNodes } from "@/api/queries/nodes";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Node } from "@/types";

const ROW_LIMIT = 50;

// Auslastungs-Ampel (Design): offline grau, >85% rot, >65% gelb, sonst grün.
function utilTone(n: Node): { bar: string; pct: number } {
  const pct = n.capacity.maxAgents > 0 ? Math.round((n.runningAgents / n.capacity.maxAgents) * 100) : 0;
  if (n.status === "offline") return { bar: "bg-muted", pct: 0 };
  if (pct > 85) return { bar: "bg-danger", pct };
  if (pct > 65) return { bar: "bg-warn", pct };
  return { bar: "bg-ok", pct };
}

function NodeCard({ node }: { node: Node }) {
  const { bar, pct } = utilTone(node);
  const tiles: Array<[string, string]> = [
    ["CPU", `${node.capacity.cpu} vCPU`],
    ["RAM", `${node.capacity.memoryGb} GB`],
    ["Backend", node.backends.join(", ") || "—"],
  ];
  return (
    <Link
      to={`/nodes/${node.id}`}
      data-clickable="true"
      className="block overflow-hidden rounded border border-border bg-surface shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="p-[18px]">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] border border-border bg-surface2 text-text3">
              <Server className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="mono text-[15px] font-bold text-text">{node.hostname}</div>
              <div className="mono text-[11.5px] text-text3">{node.id}</div>
            </div>
          </div>
          <Badge variant={node.status === "online" ? "online" : "offline"}>
            {node.status === "online" && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
            {node.status}
          </Badge>
        </div>

        {/* Auslastung */}
        <div className="mb-3.5">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[12.5px] font-semibold text-text2">Auslastung</span>
            <span className="tnum text-[12.5px] text-text3">
              {node.status === "offline" ? "—" : `${node.runningAgents} / ${node.capacity.maxAgents} Agents`}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-surface3">
            <div className={cn("h-full rounded-full transition-all", bar)} style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/* Kacheln */}
        <div className="grid grid-cols-3 gap-2.5">
          {tiles.map(([label, value]) => (
            <div key={label} className="rounded-lg bg-surface2 px-3 py-2.5">
              <div className="text-[10.5px] font-bold uppercase tracking-wider text-text3">{label}</div>
              <div className="mono mt-0.5 truncate text-[13px] font-semibold text-text" title={value}>{value}</div>
            </div>
          ))}
        </div>
      </div>
    </Link>
  );
}

export function NodesList() {
  const { data, isLoading, isError, error, refetch } = useNodes();
  const rows = (data ?? []).slice(0, ROW_LIMIT);
  const onlineCount = (data ?? []).filter((n) => n.status === "online").length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-[13px] text-text3">
          {data?.length ?? 0} Worker-Nodes · {onlineCount} online
        </p>
      </div>

      {isLoading && !data ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-[188px] animate-pulse rounded border border-border bg-surface2" />
          ))}
        </div>
      ) : isError ? (
        <div className="flex items-start gap-3 rounded border border-border bg-surface p-6 shadow-sm" data-testid="list-error">
          <AlertCircle className="mt-0.5 h-5 w-5 text-danger" />
          <div className="flex-1">
            <div className="font-medium text-danger">Fehler beim Laden</div>
            <div className="mt-1 text-sm text-text3">{(error as Error | undefined)?.message ?? "Unbekannter Fehler"}</div>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="list-retry">
            <RefreshCw className="h-4 w-4" />Retry
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded border border-border bg-surface p-12 text-center text-text3 shadow-sm" data-testid="list-empty">
          <div className="text-base">Noch keine Nodes</div>
          <div className="mt-2 text-xs text-text3">Node-Enrollment via POST /nodes/enroll</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {rows.map((n) => <NodeCard key={n.id} node={n} />)}
        </div>
      )}
    </div>
  );
}
